// ═══════════════════════════════════════════════════════════════════════════
// CATÉGORIE EMPLOYEUR ONSS — quand a-t-on le droit de l'écrire, et quand pas.
//
// POURQUOI CE FICHIER EXISTE (17/08/2026). Le fondateur génère l'annexe-C4
// d'Eloïse De Smet et trouve le bloc employeur vide. En le réparant, on tombe
// sur pire que le vide : la valeur qu'on s'apprêtait à écrire est FAUSSE.
//
// `clients.data.employer_class` vaut **210** pour GeeksDen ET pour Deep Place.
// 210 est la catégorie d'AUREUS. Notre propre robot `verif-employeur` le
// documente noir sur blanc depuis le 16/07/2026 :
//
//     « employer_class absent partout → fallback silencieux sur Aureus (210) »
//     « Ce qui reste vraiment non sourçable (catégorie employeur = écran DmfA
//       Web), il le DIT et indique où la trouver, au lieu d'inventer une valeur. »
//
// Le repli a donc FUI en base, et il a l'air d'une donnée. Le recopier sur un
// formulaire ONEM, c'est déclarer à l'État que le client relève de la catégorie
// d'Aureus. Un vide se voit et se complète à la main ; un 210 faux se signe.
//
// ⚠️ CE FICHIER NE DEVINE RIEN. Il répond à une seule question : « peut-on
// écrire cette valeur sur un document officiel ? ». Quand la réponse est non,
// il dit POURQUOI et OÙ la trouver — c'est l'écran DmfA Web de l'ONSS.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Catégorie ONSS d'Aureus IA. Elle sert de repli dans le générateur DmfA (par
 * construction : quand on déclare POUR Aureus, c'est la bonne). Ailleurs, la
 * voir sur un dossier client est le signe du repli qui a fui — pas une donnée.
 */
export const CATEGORIE_AUREUS = '210';

/** Où le fondateur va la chercher quand on refuse de l'écrire. */
export const OU_TROUVER = 'écran DmfA Web de l’ONSS (déclaration trimestrielle), rubrique employeur';

/**
 * Le document qui ÉTABLIT la catégorie d'un dossier client, ou null.
 *
 * 🔴 02/10/2026, BD Motion. La catégorie 210 est LÉGITIME pour de nombreux
 * employeurs : sa définition ONSS est « catégorie générale » (lettre d'attribution
 * de la qualité d'employeur du 03/08/2026 : indice 210). Le garde-fou ci-dessous
 * la refuse sur tout dossier non-Aureus parce qu'elle a déjà fui comme repli — mais
 * il refusait donc AUSSI un vrai 210, et le C4 des deux étudiantes de BD Motion
 * serait parti avec un champ vide alors que la bonne valeur était écrite sur la
 * lettre de l'ONSS.
 *
 * Un repli qui a fui et une vraie valeur se ressemblent à l'octet près : seule
 * une SOURCE les distingue. `employer_class_source` (colonne ou `data`) cite le
 * document officiel qui l'établit ; sans lui, le 210 reste refusé comme avant.
 *
 * @param {object} co  la fiche `clients` (colonnes + `data`)
 * @returns {string|null}
 */
export function sourceCategorieDocumentee(co) {
  if (!co || typeof co !== 'object') return null;
  let data = co.data;
  if (typeof data === 'string') { try { data = JSON.parse(data); } catch { data = null; } }
  const brute = co.employer_class_source || (data && data.employer_class_source) || '';
  const t = String(brute).trim();
  // Une vraie citation nomme un document et une date : on exige un minimum de
  // substance pour qu'un « oui » ou un point ne suffise pas à ouvrir la porte.
  return t.length >= 15 ? t : null;
}

/**
 * Peut-on écrire cette catégorie sur un formulaire officiel ?
 *
 * @param {string|number} valeur       ce que porte le dossier
 * @param {boolean} estDossierAureus   le dossier est-il Aureus lui-même ?
 * @returns {{ ecrire: string|null, raison: string|null }}
 *          `ecrire` = la valeur à poser, ou null ; `raison` = pourquoi pas.
 */
export function categorieEmployeurUtilisable(valeur, estDossierAureus = false, sourceDocument = null) {
  const v = String(valeur ?? '').trim();
  if (!v) {
    return { ecrire: null, raison: `catégorie employeur absente du dossier — à lire sur l’${OU_TROUVER}` };
  }
  if (v === CATEGORIE_AUREUS && !estDossierAureus && !sourceDocument) {
    return {
      ecrire: null,
      raison: `catégorie employeur ${CATEGORIE_AUREUS} = celle d’Aureus, repli connu qui a fui en base (cron verif-employeur, 16/07/2026) — la vraie se lit sur l’${OU_TROUVER}. Si l’ONSS l’a bien attribuée à cet employeur (lettre d’attribution de la qualité d’employeur), enregistrez-la avec sa source (employer_class_source).`,
    };
  }
  // Les catégories ONSS sont numériques et tiennent sur 3 chiffres. Une valeur
  // d'une autre forme n'est pas une catégorie : on ne la recopie pas non plus.
  if (!/^\d{1,3}$/.test(v)) {
    return { ecrire: null, raison: `« ${v} » n’a pas la forme d’une catégorie employeur ONSS (1 à 3 chiffres)` };
  }
  return { ecrire: v, raison: null };
}


/**
 * La catégorie à écrire sur un document officiel, entre ce que la BASE porte et
 * ce que l'ÉCRAN a relayé.
 *
 * ⚠️ LA BASE D'ABORD, TOUJOURS. Mesuré le 18/08/2026 : la base portait 017
 * (encodé et confirmé par SELECT), mais l'état du navigateur portait encore le
 * 210 d'Aureus — et l'écran l'a relayé. Le C4, sans garde-fou, est parti chez
 * le client avec la catégorie de son prestataire ; l'annexe, gardée, l'a
 * refusé et est partie VIDE. Le dossier relu à l'instant par le serveur fait
 * foi ; la valeur d'écran ne sert que si la base n'a rien.
 *
 * @param {string|number} depuisLaBase  ce que le dossier client porte (relu serveur)
 * @param {string|number} depuisLEcran  ce que l'écran a relayé
 * @param {boolean} estDossierAureus
 * @param {string|null} sourceDocument
 * @returns {{ ecrire: string|null, raison: string|null }}
 */
export function resoudreCategorieEmployeur(depuisLaBase, depuisLEcran, estDossierAureus = false, sourceDocument = null) {
  // La source documentée ne vaut QUE pour la valeur de la base : une valeur
  // relayée par l'écran ne peut pas se réclamer d'un document qu'elle n'a pas.
  const base = categorieEmployeurUtilisable(depuisLaBase, estDossierAureus, sourceDocument);
  if (base.ecrire) return base;
  const ecran = categorieEmployeurUtilisable(depuisLEcran, estDossierAureus);
  if (ecran.ecrire) return ecran;
  // Aucune des deux n'est écrivable : la raison de la BASE est la plus utile
  // (c'est elle qu'il faut compléter), sauf si la base était simplement vide.
  return String(depuisLaBase ?? '').trim() ? base : ecran;
}


// ═══════════════════════════════════════════════════════════════════════════
// 🖨️ CE QU'ON IMPRIME QUAND ON N'A PAS LA CATÉGORIE (25/08/2026)
//
// Découvert en branchant la catégorie sur le taux de fonds sectoriel. Deux
// surfaces imprimaient la catégorie d'employeur sur des documents qui partent
// chez le travailleur — la fiche de paie PDF et les documents générés — et
// toutes deux écrivaient la même chose (les guillemets français remplacent ici
// les apostrophes du code : le balayage `code-travailleur-une-seule-porte`
// cherche les codes travailleur entre quotes, et cette CITATION n'est pas une
// décision — la lui donner telle quelle ferait entrer ce fichier dans sa liste
// de dette, qui ne doit contenir que de la vraie dette) :
//
//     co.categorie_onss || co.onss_categorie || «495»
//
// Deux fautes en une ligne, et elles se cumulent :
//
//   1. **Aucun de ces deux noms de champ n'existe.** La catégorie d'employeur
//      vit dans `employer_class` (colonne ou `data`), et c'est ce que lisent
//      `dmfa-readiness.js`, `verif-employeur-engine.js`, `drs-prefill.js` et le
//      générateur DmfA. Le repli tombait donc à TOUS LES COUPS.
//   2. **495 n'est pas une catégorie d'employeur.** C'est un CODE TRAVAILLEUR
//      (`ContributionWorkerCode`, annexe 2 : « travailleurs intellectuels —
//      employés ordinaires »). Vérifié sur l'annexe 27, la liste officielle des
//      catégories d'employeur : 494, 496 et 497 y sont, 495 n'y est pas.
//
// Chaque fiche de paie annonçait donc au travailleur, comme catégorie ONSS de
// son employeur, un code qui désigne le travailleur lui-même. C'est la même
// famille que le 210 d'Aureus qui a fui en base — un repli qui a pris l'air
// d'une donnée — et la même réponse : **un champ vide se complète, un chiffre
// faux se signe.**
// ═══════════════════════════════════════════════════════════════════════════

/** Les noms sous lesquels la catégorie d'employeur peut vivre sur un dossier. */
const NOMS_DE_CHAMP = ['employer_class', 'employerClass', 'categorieEmployeur', 'categorie_onss', 'onss_categorie'];

/** La catégorie brute portée par le dossier — colonne d'abord, `data` ensuite. */
export function lireCategorieEmployeur(co) {
  const o = co || {};
  for (const n of NOMS_DE_CHAMP) {
    const v = o[n];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  const d = o.data || {};
  for (const n of NOMS_DE_CHAMP) {
    const v = d[n];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return null;
}

/** Ce qu'on écrit à la place quand on n'a rien d'écrivable (R8 : FR + NL). */
const A_COMPLETER = {
  fr: '(à compléter — écran DmfA Web de l’ONSS)',
  nl: '(aan te vullen — DmfA Web-scherm van de RSZ)',
};

/**
 * Le texte à imprimer pour la catégorie d'employeur, sur un document ou un écran.
 *
 * @param {object} co  le dossier client
 * @param {{estDossierAureus?: boolean, langue?: 'fr'|'nl'}} options
 * @returns {{valeur: string|null, texte: string, complete: boolean, raison: string|null}}
 */
export function categorieEmployeurAffichable(co, { estDossierAureus = false, langue = 'fr' } = {}) {
  const brut = lireCategorieEmployeur(co);
  const { ecrire, raison } = categorieEmployeurUtilisable(brut, estDossierAureus);
  if (ecrire) return { valeur: ecrire, texte: ecrire, complete: true, raison: null };
  return { valeur: null, texte: A_COMPLETER[langue] || A_COMPLETER.fr, complete: false, raison };
}
